import { Book } from '../services/adminApi';

export interface BibleResource {
  id: string;
  name: string;
  category: 'bible-apps' | 'education' | 'ministry-media' | 'study-materials' | 'youtube-ministries';
  url: string;
  descriptionEn: string;
  descriptionEs: string;
  tag?: string;
}

export interface ResourceCategory {
  id: 'all' | 'bible-apps' | 'education' | 'ministry-media' | 'study-materials' | 'youtube-ministries';
  labelKey: string;
  fallbackLabel: string;
  descriptionKey: string;
  fallbackDescription: string;
}

export const RESOURCE_CATEGORIES: ResourceCategory[] = [
  {
    id: 'all',
    labelKey: 'resourcesPage.categories.all',
    fallbackLabel: 'All Resources',
    descriptionKey: 'resourcesPage.categories.allDesc',
    fallbackDescription: 'Explore the full directory of curated Christian study tools and ministries.'
  },
  {
    id: 'bible-apps',
    labelKey: 'resourcesPage.categories.bibleApps',
    fallbackLabel: 'Bible Apps & Study Platforms',
    descriptionKey: 'resourcesPage.categories.bibleAppsDesc',
    fallbackDescription: 'Online scriptures, study tools, concordances, and multi-translation platforms.'
  },
  {
    id: 'education',
    labelKey: 'resourcesPage.categories.education',
    fallbackLabel: 'Education & Courses',
    descriptionKey: 'resourcesPage.categories.educationDesc',
    fallbackDescription: 'Seminaries, free theological courses, and university level learning.'
  },
  {
    id: 'ministry-media',
    labelKey: 'resourcesPage.categories.ministryMedia',
    fallbackLabel: 'Ministry & Media',
    descriptionKey: 'resourcesPage.categories.ministryMediaDesc',
    fallbackDescription: 'Christian media productions, open-source technology, and evangelism organizations.'
  },
  {
    id: 'study-materials',
    labelKey: 'resourcesPage.categories.studyMaterials',
    fallbackLabel: 'Study Materials',
    descriptionKey: 'resourcesPage.categories.studyMaterialsDesc',
    fallbackDescription: 'Commentaries, study Bibles, and practical reference texts.'
  },
  {
    id: 'youtube-ministries',
    labelKey: 'resourcesPage.categories.youtubeMinistries',
    fallbackLabel: 'YouTube & Ministries',
    descriptionKey: 'resourcesPage.categories.youtubeMinistriesDesc',
    fallbackDescription: 'Apologetics channels, preaching, verse-by-verse teachings, and cultural discernment.'
  }
];

export const BIBLE_STUDY_RESOURCES: BibleResource[] = [
  // Bible Apps & Study
  {
    id: 'bible-gateway',
    name: 'Bible Gateway',
    category: 'bible-apps',
    url: 'https://www.biblegateway.com',
    descriptionEn: 'Searchable online Bible with over 200 translations, audio Bibles, and commentary tools.',
    descriptionEs: 'Biblia en línea con más de 200 traducciones, audiolibros bíblicos y herramientas de comentarios.',
    tag: 'App'
  },
  {
    id: 'youversion',
    name: 'YouVersion Bible App',
    category: 'bible-apps',
    url: 'https://www.youversion.com/the-bible-app/',
    descriptionEn: 'The world’s most downloaded Bible app featuring daily reading plans, audio, and community notes.',
    descriptionEs: 'La aplicación bíblica más descargada con planes diarios de lectura, audio y notas en comunidad.',
    tag: 'App'
  },
  {
    id: 'bible-hub',
    name: 'Bible Hub',
    category: 'bible-apps',
    url: 'https://biblehub.com/',
    descriptionEn: 'Deep-dive study suite offering Hebrew/Greek interlinear, Strong’s concordance, and cross-references.',
    descriptionEs: 'Conjunto de estudio profundo con interlineal hebreo/griego, concordancia Strong y referencias cruzadas.',
    tag: 'Interlinear & Greek/Hebrew'
  },
  {
    id: 'bible-study-tools',
    name: 'Bible Study Tools',
    category: 'bible-apps',
    url: 'https://biblestudytools.com/',
    descriptionEn: 'Vast digital library featuring classic commentaries, Bible dictionaries, and reading resources.',
    descriptionEs: 'Amplia biblioteca digital con comentarios clásicos, diccionarios bíblicos y recursos de lectura.',
    tag: 'Commentaries'
  },
  {
    id: 'biblia-com',
    name: 'Biblia.com',
    category: 'bible-apps',
    url: 'https://biblia.com/books/nasb95/Jn14',
    descriptionEn: 'Clean digital Bible reader and reference platform powered by Faithlife.',
    descriptionEs: 'Lector bíblico digital limpio y plataforma de referencia impulsada por Faithlife.',
    tag: 'Web Reader'
  },
  {
    id: 'paper-bible',
    name: 'Paper Bible (Print Scripture)',
    category: 'bible-apps',
    url: 'https://paper.bible/',
    descriptionEn: 'Distraction-free printable scripture portions tailored for personal study, note-taking, and journaling.',
    descriptionEs: 'Porciones de las Escrituras imprimibles y sin distracciones, ideales para estudio personal y notas.',
    tag: 'Print Scripture'
  },

  // Ministry & Media
  {
    id: 'living-waters',
    name: 'Living Waters',
    category: 'ministry-media',
    url: 'https://livingwaters.com/',
    descriptionEn: 'Evangelism training, biblical literature, Gospel tracts, and documentary films from Ray Comfort.',
    descriptionEs: 'Capacitación en evangelismo, literatura bíblica, tratados del Evangelio y documentales de Ray Comfort.',
    tag: 'Evangelism'
  },
  {
    id: 'the-chosen',
    name: 'The Chosen',
    category: 'ministry-media',
    url: 'https://watch.thechosen.tv/',
    descriptionEn: 'Acclaimed multi-season historical series depicting the authentic life and ministry of Jesus Christ.',
    descriptionEs: 'Aclamada serie histórica de varias temporadas que retrata la vida y el ministerio de Jesucristo.',
    tag: 'Video Series'
  },
  {
    id: 'good-fight-ministries-site',
    name: 'Good Fight Ministries',
    category: 'ministry-media',
    url: 'https://www.goodfight.org/sold-souls-free/',
    descriptionEn: 'Equipping the body of Christ with biblical discernment, cultural analysis, and apologetics.',
    descriptionEs: 'Equipando al cuerpo de Cristo con discernimiento bíblico, análisis cultural y apologética.',
    tag: 'Discernment'
  },
  {
    id: 'gracious-tech',
    name: 'Gracious Tech',
    category: 'ministry-media',
    url: 'https://gracious.tech/',
    descriptionEn: 'Open-source software, datasets, and modern developer tools advancing global Bible access.',
    descriptionEs: 'Software de código abierto y herramientas de desarrollo para avanzar el acceso global a la Biblia.',
    tag: 'Open Source'
  },

  // Study Materials
  {
    id: 'life-application-study-bible',
    name: 'Life Application Study Bible',
    category: 'study-materials',
    url: 'https://www.tyndale.com/sites/lasb/?srsltid=AfmBOopCe9OY-LiBxGYmSnnfXTSe8_EO4ICfWPCLzv6NH2n7RwZTIww0',
    descriptionEn: 'Premier study Bible designed to bridge the gap between biblical truth and real-life daily application.',
    descriptionEs: 'Destacada Biblia de estudio diseñada para conectar la verdad bíblica con la aplicación práctica diaria.',
    tag: 'Study Bible'
  },

  // Education & Courses
  {
    id: 'biola-university',
    name: 'Biola University Learn',
    category: 'education',
    url: 'https://www.biola.edu/learn',
    descriptionEn: 'Free Christian courses, video lectures, and theological insights from renowned university scholars.',
    descriptionEs: 'Cursos cristianos gratuitos, conferencias en video e ideas teológicas de destacados profesores.',
    tag: 'Seminary & University'
  },
  {
    id: 'southern-evangelical-seminary',
    name: 'Southern Evangelical Seminary',
    category: 'education',
    url: 'https://ses.edu/',
    descriptionEn: 'Classical apologetics and rigorous theological education defending the Christian worldview.',
    descriptionEs: 'Educación teológica rigurosa y apologética clásica en defensa de la cosmovisión cristiana.',
    tag: 'Seminary'
  },
  {
    id: 'dallas-theological-seminary',
    name: 'Dallas Theological Seminary Courses',
    category: 'education',
    url: 'https://courses.dts.edu/',
    descriptionEn: 'Free online theological training, verse-by-verse courses, and systematic theology classes.',
    descriptionEs: 'Capacitación teológica gratuita en línea, cursos versículo por versículo y teología sistemática.',
    tag: 'Free Courses'
  },
  {
    id: 'christianleaders-org',
    name: 'Christianleaders.org',
    category: 'education',
    url: 'https://christianleaders.org/course/view.php?id=524',
    descriptionEn: 'Tuition-free ministry certificates, degree programs, and leadership training worldwide.',
    descriptionEs: 'Certificados ministeriales gratuitos, programas de grado y capacitación de liderazgo en todo el mundo.',
    tag: 'Leadership Training'
  },
  {
    id: 'gospel-coalition-courses',
    name: 'Gospel Coalition Courses',
    category: 'education',
    url: 'https://www.thegospelcoalition.org/courses/',
    descriptionEn: 'Structured learning pathways covering biblical doctrine, ethics, church history, and pastoral care.',
    descriptionEs: 'Rutas de aprendizaje estructuradas sobre doctrina bíblica, ética, historia de la iglesia y cuidado pastoral.',
    tag: 'Biblical Courses'
  },
  {
    id: 'wvbs-online-bible-school',
    name: 'WVBS Online Bible School',
    category: 'education',
    url: 'https://school.wvbs.org/',
    descriptionEn: 'Free in-depth Bible instruction and online video curriculum covering Old and New Testament books and core biblical topics.',
    descriptionEs: 'Instrucción bíblica profunda y gratuita con currículo en video en línea que cubre libros del Antiguo y Nuevo Testamento.',
    tag: 'Online Bible School'
  },
  {
    id: 'bsf-online',
    name: 'BSF (Bible Study Fellowship)',
    category: 'education',
    url: 'https://www.bsfonline.org/',
    descriptionEn: 'In-depth, structured interdenominational Bible studies, weekly lessons, and global online discussion groups.',
    descriptionEs: 'Estudios bíblicos estructurados e interdenominacionales, lecciones semanales y grupos globales de estudio.',
    tag: 'Global Bible Study'
  },
  {
    id: 'cs-lewis-institute-courses',
    name: 'C.S. Lewis Institute Courses',
    category: 'education',
    url: 'https://cslewiscourses.org/',
    descriptionEn: 'Free online Christian study courses, discipleship training, apologetics, and spiritual formation from the C.S. Lewis Institute.',
    descriptionEs: 'Cursos de estudio cristiano gratuitos en línea, discipulado, apologética y formación espiritual del Instituto C.S. Lewis.',
    tag: 'Discipleship Courses'
  },

  // YouTube & Ministries
  {
    id: 'cross-examined',
    name: 'Cross Examined',
    category: 'youtube-ministries',
    url: 'https://www.youtube.com/channel/UCedYGs_lqq1uNet0u7qlSyQ',
    descriptionEn: 'Dr. Frank Turek answering tough questions with intellectual, evidence-based Christian apologetics.',
    descriptionEs: 'El Dr. Frank Turek respondiendo preguntas difíciles con apologética cristiana basada en evidencias.',
    tag: 'Apologetics'
  },
  {
    id: 'ray-comfort',
    name: 'Ray Comfort (Just Witnessing)',
    category: 'youtube-ministries',
    url: 'https://www.youtube.com/@raycomfortjustwitnessing',
    descriptionEn: 'Real-world evangelism conversations, law and gospel teachings, and biblical witness.',
    descriptionEs: 'Conversaciones de evangelismo en la vida real, enseñanzas sobre la ley y el evangelio.',
    tag: 'Evangelism'
  },
  {
    id: 'the-bible-project',
    name: 'The Bible Project',
    category: 'youtube-ministries',
    url: 'https://www.youtube.com/user/theBibleProject',
    descriptionEn: 'Beautiful visual animations exploring every book and key theological theme across the biblical narrative.',
    descriptionEs: 'Hermosas animaciones visuales que exploran cada libro y tema teológico en la narrativa bíblica.',
    tag: 'Animations & Theology'
  },
  {
    id: 'john-jorgenson',
    name: 'John Jorgenson',
    category: 'youtube-ministries',
    url: 'https://www.youtube.com/@JonJorgensonVideos',
    descriptionEn: 'Creative spoken word, inspirational devotionals, and conversations centered around Jesus.',
    descriptionEs: 'Palabra hablada creativa, devocionales inspiradores y reflexiones centradas en Jesús.',
    tag: 'Spoken Word'
  },
  {
    id: 'calvary-chapel-fort-lauderdale',
    name: 'Calvary Chapel Fort Lauderdale',
    category: 'youtube-ministries',
    url: 'https://www.youtube.com/@calvaryftl',
    descriptionEn: 'Practical verse-by-verse expository Bible preaching, uplifting worship, and spiritual encouragement.',
    descriptionEs: 'Predicación expositiva bíblica versículo por versículo, adoración y ánimo espiritual.',
    tag: 'Expository Preaching'
  },
  {
    id: 'living-waters-channel',
    name: 'Living Waters Channel',
    category: 'youtube-ministries',
    url: 'https://www.youtube.com/c/LivingWaters',
    descriptionEn: 'Equipping believers to effectively communicate Christ, answer skeptics, and preach the Gospel.',
    descriptionEs: 'Equipando a los creyentes para comunicar eficazmente a Cristo y responder a los escépticos.',
    tag: 'Outreach & Teaching'
  },
  {
    id: 'the-beat-allen-parr',
    name: 'THE BEAT by Allen Parr',
    category: 'youtube-ministries',
    url: 'https://www.youtube.com/@thebeatagp',
    descriptionEn: 'Engaging biblical commentary, theological clarity, and answers to pressing cultural issues.',
    descriptionEs: 'Comentario bíblico claro, claridad teológica y respuestas a dilemas culturales actuales.',
    tag: 'Theology & Culture'
  },
  {
    id: 'wes-huff',
    name: 'Wes Huff',
    category: 'youtube-ministries',
    url: 'https://www.youtube.com/@WesHuff',
    descriptionEn: 'Textual scholar defending biblical reliability, manuscript evidence, and historical authenticity.',
    descriptionEs: 'Académico textual que defiende la confiabilidad bíblica y la evidencia de manuscritos.',
    tag: 'Biblical Manuscripts'
  },
  {
    id: 'melissa-dougherty',
    name: 'Melissa Dougherty',
    category: 'youtube-ministries',
    url: 'https://www.youtube.com/@MelissaDougherty',
    descriptionEn: 'Christian apologetics, satirical breakdowns, and defense of historic orthodox Christian doctrine.',
    descriptionEs: 'Apologética cristiana, análisis con humor y defensa de la doctrina cristiana histórica.',
    tag: 'Discernment & Apologetics'
  },
  {
    id: 'good-fight-ministries-channel',
    name: 'Good Fight Ministries Channel',
    category: 'youtube-ministries',
    url: 'https://www.youtube.com/@goodfightministries',
    descriptionEn: 'In-depth documentaries, media exposés, and scriptural truth examining popular cultural movements.',
    descriptionEs: 'Documentales detallados, análisis de medios y verdad bíblica examinando la cultura popular.',
    tag: 'Documentaries'
  },
  {
    id: 'cold-case-christianity',
    name: 'Cold Case Christianity',
    category: 'youtube-ministries',
    url: 'https://coldcasechristianity.com/j-warner-wallace-christian-apologist-and-author/',
    descriptionEn: 'Homicide detective J. Warner Wallace using investigative evidence to validate the New Testament.',
    descriptionEs: 'El detective J. Warner Wallace utilizando métodos forenses para validar los evangelios.',
    tag: 'Forensic Apologetics'
  },
  {
    id: 'ear-to-hear',
    name: 'Ear to Hear',
    category: 'youtube-ministries',
    url: 'https://www.youtube.com/@eartohear',
    descriptionEn: 'Biblical readings, classic Christian sermons, and audio expositions for the hungry believer.',
    descriptionEs: 'Lecturas bíblicas, sermones clásicos y exposiciones en audio para alimentar la fe.',
    tag: 'Audio Exposition'
  },
  {
    id: 'sean-mcdowell',
    name: 'Sean McDowell',
    category: 'youtube-ministries',
    url: 'https://seanmcdowell.org/',
    descriptionEn: 'Thoughtful dialogues on culture, theology, ethics, and reasons for believing in Jesus.',
    descriptionEs: 'Diálogos reflexivos sobre cultura, teología, ética y razones para creer en Jesús.',
    tag: 'Dialogue & Worldview'
  },
  {
    id: 'justin-peters',
    name: 'Justin Peters',
    category: 'youtube-ministries',
    url: 'https://www.youtube.com/@JustinPetersMin',
    descriptionEn: 'Sound biblical teaching, exposition of Scripture, and exposure of false prosperity gospel doctrines.',
    descriptionEs: 'Enseñanza bíblica sólida, exposición de la Palabra y defensa contra falsas doctrinas.',
    tag: 'Sound Doctrine'
  },
  {
    id: 'centro-evangelico-vida-nueva',
    name: 'Centro Evangelico Vida Nueva',
    category: 'youtube-ministries',
    url: 'https://youtu.be/uBayR05WIH0?si=GRjxQq9Q7uHHcH8Z',
    descriptionEn: 'Inspiring live Christian services, heartfelt worship, and expository biblical preaching.',
    descriptionEs: 'Inspiradores servicios cristianos en vivo, adoración sincera y predicación bíblica expositiva.',
    tag: 'Church Service'
  },
  {
    id: 'blessed-hope-chapel',
    name: 'Blessed Hope Chapel',
    category: 'youtube-ministries',
    url: 'https://www.youtube.com/@BlessedHopeChapel',
    descriptionEn: 'Verse-by-verse teachings, prophetic studies, and worship services led by Pastor Joe Schimmel.',
    descriptionEs: 'Enseñanzas versículo por versículo, estudios proféticos y cultos dirigidos por el pastor Joe Schimmel.',
    tag: 'Church Service'
  },
  {
    id: 'lakepointe-church',
    name: 'Lakepointe Church',
    category: 'youtube-ministries',
    url: 'https://www.youtube.com/@LakepointeChurch',
    descriptionEn: 'Dynamic weekly church services, life-changing biblical messages, and uplifting worship.',
    descriptionEs: 'Servicios semanales dinámicos, mensajes bíblicos transformadores y adoración inspiradora.',
    tag: 'Church Service'
  },
  {
    id: 'the-church-of-eleven22',
    name: 'The Church of Eleven22',
    category: 'youtube-ministries',
    url: 'https://www.youtube.com/@TheChurchofEleven22',
    descriptionEn: 'A movement for all people to discover and deepen a relationship with Jesus Christ through powerful preaching.',
    descriptionEs: 'Un movimiento para descubrir y profundizar una relación con Jesucristo a través de predicación poderosa.',
    tag: 'Church Service'
  }
];

export const DEFAULT_RECOMMENDED_BOOKS: Book[] = [
  {
    _id: "6a3dc59e8ccb21daa5550756",
    title: "I Don't Have Enough Faith to Be an Atheist",
    author: "Frank Turek",
    imageUrl: "https://res.cloudinary.com/dt4c26vuq/image/upload/v1782435635/swhjpkmrooggw8llhdck.png",
    order: 1,
    isActive: true,
    buyUrl: "https://www.christreignsmedia.io/ebook-viewer?author=Norman%20L.%20Geisler%20%26%20Frank%20Turek&ebookUrl=%2Fapi%2Febook-proxy%3Furl%3Dhttps%253A%252F%252Fdrive.google.com%252Fuc%253Fexport%253Ddownload%2526id%253D1wATK3Abvaetk6ARhsW3TdXRPYXkoU7jc&title=I%20Don%27t%20Have%20Enough%20Faith%20to%20Be%20an%20Atheist"
  },
  {
    _id: "6a3dc59e8ccb21daa5550757",
    title: "The Case for Christ",
    author: "Lee Strobel",
    imageUrl: "https://res.cloudinary.com/dt4c26vuq/image/upload/v1782435673/rhzzvuizsdbottr6kcck.png",
    order: 2,
    isActive: true,
    buyUrl: "https://www.christreignsmedia.io/ebook-viewer?author=Lee%20Strobel&ebookUrl=%2Fapi%2Febook-proxy%3Furl%3Dhttps%253A%252F%252Fdrive.google.com%252Fuc%253Fexport%253Ddownload%2526id%253D1N8VaDpGRZ1GQGC7_1MrClNBOiotp8Kx8&title=The%20Case%20for%20Christ"
  },
  {
    _id: "6a3dc59e8ccb21daa5550758",
    title: "Stealing from God",
    author: "Frank Turek",
    imageUrl: "https://res.cloudinary.com/dt4c26vuq/image/upload/v1782435706/sjokgwux7jqyndctre2o.png",
    order: 3,
    isActive: true,
    buyUrl: "https://www.christreignsmedia.io/ebook-viewer?author=Frank%20Turek&ebookUrl=%2Fapi%2Febook-proxy%3Furl%3Dhttps%253A%252F%252Fdrive.google.com%252Fuc%253Fexport%253Ddownload%2526id%253D1BDaD-O897Hc7-2MtNCS7Q0wBdi4s4q-6&title=Stealing%20from%20God"
  },
  {
    _id: "6a3dc59e8ccb21daa5550759",
    title: "Mere Christianity",
    author: "C.S. Lewis",
    imageUrl: "https://res.cloudinary.com/dt4c26vuq/image/upload/v1782435741/uihm5m33tyjocbfe5fg2.png",
    order: 4,
    isActive: true,
    buyUrl: "https://www.christreignsmedia.io/ebook-viewer?author=C.S.%20Lewis&ebookUrl=%2Fapi%2Febook-proxy%3Furl%3Dhttps%253A%252F%252Fdrive.google.com%252Fuc%253Fexport%253Ddownload%2526id%253D1WJUYXnTTi8D5IM01XoMRvIEgRxkXuLQI&title=Mere%20Christianity"
  },
  {
    _id: "6a3dc59e8ccb21daa555075a",
    title: "Evidence That Demands a Verdict",
    author: "Josh McDowell",
    imageUrl: "https://res.cloudinary.com/dt4c26vuq/image/upload/v1782435774/t8drbwad69rcf8j2klrc.png",
    order: 5,
    isActive: true,
    buyUrl: "https://www.christreignsmedia.io/pdf-viewer?author=Josh%20McDowell%2C%20Sean%20McDowell&pdfUrl=https%3A%2F%2Farchive.org%2Fdetails%2Fmc-dowell-josh-mc-dowell-sean-evidence-that-demands-a-verdict%2Fmode%2F2up%3Fview%3Dtheater"
  },
  {
    _id: "6a3dc59e8ccb21daa555075b",
    title: "Cold-Case Christianity",
    author: "J. Warner Wallace",
    imageUrl: "https://res.cloudinary.com/dt4c26vuq/image/upload/v1782435804/limntxgzaekvcuyc8asd.png",
    order: 6,
    isActive: true,
    buyUrl: "https://www.christreignsmedia.io/pdf-viewer?author=J.%20Warner%20Wallace&pdfUrl=https%3A%2F%2Farchive.org%2Fdetails%2Fcold-case-christianity-a-homicide-detective-investigates-the-claims-of-the-gospels%2Fmode%2F2up%3Fview%3Dtheater"
  }
];
